import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReviewManager = ({ onClose }) => {
  const { sessionToken } = useUnifiedAuth();
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [ratingFilter, setRatingFilter] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    fetchReviews();
  }, [sortBy, sortOrder, ratingFilter, page]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        skip: page * limit,
        limit,
      });
      if (ratingFilter) params.append('rating', ratingFilter);

      const res = await fetch(`${BACKEND_URL}/api/admin/reviews?${params}`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      const data = await res.json();
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) return;
    setDeleting(reviewId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        setReviews((prev) => prev.filter((r) => r.id !== reviewId));
        setTotal((prev) => prev - 1);
      }
    } catch (err) {
      console.error('Error deleting review:', err);
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const stars = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-review-manager">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Review Management</h2>
            <p className="text-sm text-gray-500 mt-0.5">{total} total review{total !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-review-manager">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 px-5 py-3 border-b bg-gray-50">
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            data-testid="sort-by-select"
          >
            <option value="date">Sort by Date</option>
            <option value="rating">Sort by Rating</option>
            <option value="provider">Sort by Provider</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value); setPage(0); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="desc">Newest / Highest</option>
            <option value="asc">Oldest / Lowest</option>
          </select>
          <select
            value={ratingFilter}
            onChange={(e) => { setRatingFilter(e.target.value); setPage(0); }}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            data-testid="rating-filter-select"
          >
            <option value="">All Ratings</option>
            {[1, 2, 3, 4, 5].map((r) => (
              <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>
            ))}
          </select>
        </div>

        {/* Review List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No reviews found</div>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors" data-testid="admin-review-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-amber-400 text-sm tracking-wider">{stars(review.rating)}</span>
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">
                        {review.provider_type === 'doctor' ? 'Doctor' : 'Provider'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-800">{review.provider_name || 'Unknown Provider'}</p>
                    {review.review_text && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{review.review_text}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1.5">{formatDate(review.created_at)}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(review.id)}
                    disabled={deleting === review.id}
                    className="ml-3 px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shrink-0"
                    data-testid={`delete-review-${review.id}`}
                  >
                    {deleting === review.id ? 'Deleting...' : 'Remove'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-100"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={(page + 1) * limit >= total}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewManager;
